/**
 * 데이터처리의 **예외 흐름** — 태스크가 실패했을 때 가는 길 (현장 2026-09-23).
 *
 * 정상(성공) 흐름 핸들 조금 아래에 빨간 핸들이 하나 더 생기고, 거기서 이은 노드는 **데이터처리가 실패했을 때만**
 * 실행된다(Airflow trigger_rule=one_failed, 무설치판 실행기도 같은 규칙). 성공하면 그 길은 건너뛴다.
 * 실패를 알리거나(외부명령어·SQL실행) 실패 기록을 남기는 데 쓴다.
 *
 * 값이 흐르는 길이 아니다 — 서버 변환기는 이 엣지를 **의존성으로만** 쓰고 데이터 상류로 세지 않는다
 * (backend/app/services/canvas_converter.py). DFD 검사(데이터↔처리 교대)에서도 뺀다.
 */
export const ERROR_HANDLE_ID = 'error-path';

/**
 * 예외 핸들의 자리·크기 — 노드는 양 끝이 둥글다(반지름 28px). 정상 핸들과 **같은 원** 위, 45° 아래에 둔다.
 * CSS 가 아니라 인라인으로 주는 까닭: React Flow 는 핸들의 실제 자리를 재서 선을 그리는데,
 * CSS 로 옮기면 잰 값과 어긋나 선이 핸들 중앙에서 시작하지 않는다(현장 2026-09-23).
 */
export const ERROR_HANDLE_STYLE = { top: '85%', right: 7, width: 10, height: 10, minWidth: 10, minHeight: 10 } as const;

/** 선이 내려가는 각도 — 30° */
export const ERROR_ANGLE = Math.PI / 6;

/**
 * React Flow 는 오른쪽 핸들의 **연결점을 핸들의 오른쪽 끝**으로 잡는다. 예외 핸들은 둥근 테두리 위에 놓느라
 * 안쪽으로 당겨져 있어 그 끝이 눈에 보이는 중심보다 반 지름만큼 오른쪽이다 — 선을 그릴 때 그만큼 되돌린다.
 */
export const ERROR_START_SHIFT = ERROR_HANDLE_STYLE.width / 2;

/**
 * 예외 흐름으로 이을 노드를 놓을 자리 — 정상 흐름 **아래**, 그리고 30° 로 내려갈 만큼 **오른쪽**.
 * (가로가 모자라면 선이 가팔라진다. 자리부터 각도에 맞춰 잡아 둔다)
 */
export const ERROR_TARGET_DY = 170;
export function errorTargetOffset(sourceWidth: number): { dx: number; dy: number } {
  // 손잡이(85% 높이)에서 타깃의 왼쪽 손잡이(가운데 높이)까지의 높이 차
  const dy = ERROR_TARGET_DY - 19;
  // 마지막 80px 은 수평으로 들어가는 구간 — 선 가운데의 '+' 가 노드에 붙지 않게
  return { dx: sourceWidth + Math.round(dy / Math.tan(ERROR_ANGLE)) + 80, dy: ERROR_TARGET_DY };
}

/** 이 엣지가 예외 흐름인가 (데이터처리의 아래 핸들에서 나온 것) */
export const isErrorEdge = (edge: { sourceHandle?: string | null }): boolean =>
  (edge?.sourceHandle || '') === ERROR_HANDLE_ID;

/** 이 노드가 예외 흐름을 쓰는가 — 속성에서 켰거나, 이미 그 핸들로 이은 엣지가 있다 */
export function hasErrorFlow(nodeId: string, config: any, edges: Array<{ source: string; sourceHandle?: string | null }>): boolean {
  if (config?.errorFlow) return true;
  return edges.some((e) => e.source === nodeId && isErrorEdge(e));
}
